import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	If,
	makeClassName,
	Modal,
	RelativeTimestamp,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { SlimTimelineItemLoadingPlaceholder } from "../../../../Components/LoadingPlaceholder";
import { TimelineStudentNoteListQuery } from "../../../../Utilities";
import { StudentNoteFormat } from "../../../Format";
import { StudentLink, UserLink } from "../../../Links";
import { StudentNoteIcon } from "../Icon";
import { ControlledStudentNoteModal } from "../Modal";
import styles from "./styles.module.css";

export type TimelineItemProps = {
	isLoading?: boolean;
	studentNote: TimelineStudentNoteListQuery["studentNoteConnection"]["edges"][0]["node"] | null;
} & Partial<ClassNameProps & StyleProps>;

export function TimelineItem({ isLoading = false, studentNote, className, style }: TimelineItemProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	if (isLoading && !studentNote) {
		return <SlimTimelineItemLoadingPlaceholder className={className} style={style} />;
	}

	if (!studentNote) {
		return null;
	}

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(styles["timeline-item"], "button--tile", className)}
				style={style}
			>
				<Row justify="spaced-start" align="center" horizontalSpacing="1rem">
					<StudentNoteIcon studentNote={studentNote} size={24} style={{ padding: "0.5rem" }} />

					<Column justify="spaced-start" verticalSpacing="0.125rem" style={{ width: "100%", minWidth: 0 }}>
						<Row justify="spaced-start">
							<TruncatedText className={styles["timeline-item__main-line"]}>
								{!!studentNote.author ? (
									<UserLink instance={studentNote.author} className="link--primary link--no-text-decoration" />
								) : (
									"Deleted User"
								)}{" "}
								wrote {StudentNoteFormat.noteTypeIndefiniteArticle(studentNote)}{" "}
								{StudentNoteFormat.Fields.noteType(studentNote.noteType).toLowerCase()} about{" "}
								<Conditional>
									<If condition={studentNote.primaryStudents.length === 1}>
										{studentNote.primaryStudents.length === 1 && (
											<StudentLink
												key={studentNote.primaryStudents[0].id}
												instance={studentNote.primaryStudents[0]}
												className="link--primary link--no-text-decoration"
											/>
										)}
									</If>
									<If condition={studentNote.primaryStudents.length === 2}>
										{studentNote.primaryStudents.length === 2 && (
											<React.Fragment>
												<StudentLink
													key={studentNote.primaryStudents[0].id}
													instance={studentNote.primaryStudents[0]}
													className="link--primary link--no-text-decoration"
												/>{" "}
												and{" "}
												<StudentLink
													key={studentNote.primaryStudents[1].id}
													instance={studentNote.primaryStudents[1]}
													className="link--primary link--no-text-decoration"
												/>
											</React.Fragment>
										)}
									</If>
									<Else>
										{studentNote.primaryStudents.length >= 1 && (
											<React.Fragment>
												<StudentLink
													key={studentNote.primaryStudents[0].id}
													instance={studentNote.primaryStudents[0]}
													className="link--primary link--no-text-decoration"
												/>{" "}
												and {studentNote.primaryStudents.length - 1} others
											</React.Fragment>
										)}
									</Else>
								</Conditional>
							</TruncatedText>

							<RelativeTimestamp value={studentNote.createdAt} className="timestamp nowrap" />
						</Row>

						<TruncatedText className={styles["timeline-item__preview-text"]}>
							{/* interaction between TruncatedText with `lines={1}` and FormattedText does not work */}
							{studentNote.body.split("\n")[0]}
						</TruncatedText>
					</Column>
				</Row>
			</Button>

			<Modal.If condition={isModalOpen}>
				<ControlledStudentNoteModal
					studentNoteID={studentNote.id}
					noteType={studentNote.noteType}
					onClose={closeModal}
				/>
			</Modal.If>
		</React.Fragment>
	);
}
